// Import necessary libraries
const { algoliasearch, instantsearch } = window;
const { autocomplete } = window['@algolia/autocomplete-js'];
const { createLocalStorageRecentSearchesPlugin } =
  window['@algolia/autocomplete-plugin-recent-searches'];
const { createQuerySuggestionsPlugin } =
  window['@algolia/autocomplete-plugin-query-suggestions'];
import { hitsPerPage } from "instantsearch.js/es/widgets";
import { createDropdown } from "./Dropdown";
import { clearRefinements } from "instantsearch.js/es/widgets";
import { currentRefinements } from "instantsearch.js/es/widgets";
import sitesConfig from "../sites.json";
import { initSchedule } from "./schedule";
import * as defaultFaviconAsset from "url:../favicon.png";
const defaultFaviconUrl = defaultFaviconAsset.default || defaultFaviconAsset;


/* ------------------------------------------------------------------------
 * Because we use parcel, we need to import all available files here, so that they are bundled and available for use.
 */
/* CSS files mapping */
const cssFiles = {
  tapra2026: new URL (
    "../public/tapra2026/app.css",
    import.meta.url
  ),
  hm2025: new URL (
    "../public/hm2025/app.css",
    import.meta.url
  ),
  hm2026: new URL (
    "../public/hm2026/app.css",
    import.meta.url
  ),
};

/* Sidebar files mapping */
const sidebarUrls = {
  tapra2026: new URL(
    "../public/tapra2026/sidebar.html",
    import.meta.url
  ),
  hm2025: new URL(
    "../public/hm2025/sidebar.html",
    import.meta.url
  ),
  hm2026: new URL(
    "../public/hm2026/sidebar.html",
    import.meta.url
  ),
};

/* Favicon files mapping */
const faviconFiles = {
  "favicon.png": defaultFaviconUrl,
  tapra2026: new URL(
    "../public/tapra2026/favicon.png",
    import.meta.url
  ),
  hm2025: new URL(
    "../public/hm2025/favicon.png",
    import.meta.url
  ),
  hm2026: new URL(
    "../public/hm2026/favicon.png",
    import.meta.url
  ),
};

const campusMapFiles = {
   tapra2026: new URL(
    "../public/tapra2026/campusmap.pdf",
    import.meta.url
  ),
  hm2025: new URL(
    "../public/hm2025/campusmap.pdf",
    import.meta.url
  ),
  hm2026: new URL(
    "../public/hm2026/campusmap.pdf",
    import.meta.url
  ),
};

const timetableFiles = {
  hm2026: new URL("../public/hm2026/timetable.pdf", import.meta.url),
};

/*  
 * Get the configuration for the requested site
 */
function getSiteConfig() {
  const requestedSite =
    new URLSearchParams(window.location.search).get("site") ||
    window.location.host ||
    process.env.TYPESENSE_SITE ||
    sitesConfig.defaultSite;
  const sites = sitesConfig.sites || {};
  const siteKey = sites[requestedSite] ? requestedSite : sitesConfig.defaultSite;

  if (requestedSite !== siteKey) {
    console.warn(`Site "${requestedSite}" not found. Using "${siteKey}".`);
  }

  return {
    key: siteKey,
    ...sites[siteKey],
  };
}

function applySiteConfig(siteConfig) {
  const title = siteConfig.title;
  const stylesheet = document.getElementById("site-stylesheet");
  const favicon = document.getElementById("site-favicon");

  document.title = title;
  document.getElementById("header-title-a").innerText = title;
  document.documentElement.dataset.site = siteConfig.key;

  if (stylesheet && siteConfig.publicdir) {
    stylesheet.href = cssFiles[siteConfig.publicdir];
  }
  if (favicon) {
    favicon.href = faviconFiles[siteConfig.publicdir];
  }

  const timetableUrl = timetableFiles[siteConfig.publicdir];
  if (timetableUrl) {
    document.getElementById("download-pdf-button").href = timetableUrl;
    document.getElementById("download-pdf-container").hidden = false;
  }

  // Load sidebar content if specified
  if (siteConfig.elements && siteConfig.elements.sidebar) {
    const sidebarUrl = sidebarUrls[siteConfig.publicdir];
    fetch(sidebarUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load sidebar`);
        }
        return response.text();
      })
      .then((html) => {
        const sidebarElement = document.getElementById("sidebar");

        if (sidebarElement) {
          sidebarElement.innerHTML = html;
        }
      })
      .catch((error) => {
        console.error("Error loading sidebar:", error);
      });
  }  
}

function getTimeForDateAndSlot(date, slot) {
  if (!siteConfig.schedule) {
    return null;
  }

  let d = date;
  if (!(date instanceof Date)) {
    // Parse programme labels explicitly; browser date-string parsing varies.
    const match = String(date).trim().match(/^(?:[A-Za-z]+,?\s+)?(\d{1,2})(?:st|nd|rd|th)?(?:\s+([A-Za-z]+))?$/i);
    if (!match) {
      return null;
    }

    const months = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
    const year = siteConfig.conferenceYear;
    const monthIndex = match[2]
      ? months.indexOf(match[2].toLowerCase())
      : siteConfig.conferenceMonth - 1;
    const day = Number(match[1]);
    if (!Number.isInteger(year) || !Number.isInteger(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return null;
    }

    d = new Date(year, monthIndex, day);
    // Reject invalid days instead of allowing Date to roll into another month.
    if (d.getFullYear() !== year || d.getMonth() !== monthIndex || d.getDate() !== day) {
      return null;
    }
  }

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const dayName = dayNames[d.getDay()];

  if (!siteConfig.schedule?.[dayName]) {
    return null; // e.g. Monday, Tuesday, Wednesday not in schedule
  }

  return siteConfig.schedule[dayName][slot] || null;
}

const siteConfig = getSiteConfig();
applySiteConfig(siteConfig);


// Check environment variables for Typesense
if (!process.env.TYPESENSE_API_KEY) {
  console.error('API Key not found in environmental variables.');
}

//
// Create typesense adapter
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";

const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
  server: {
    apiKey: process.env.TYPESENSE_API_KEY, // Read from environment variable
    nodes: [
      {
        host: process.env.TYPESENSE_HOST,
        port: process.env.TYPESENSE_PORT,
        protocol: process.env.TYPESENSE_PROTOCOL
      }
    ]
  },

  additionalSearchParameters: {
    query_by: "panel_title,date,building,stream,fulltext",
    sort_by: "timestamp:asc,building:desc,room:asc"
  }
});

const searchClient = typesenseInstantsearchAdapter.searchClient;
const personalSchedule = initSchedule({
  siteConfig,
  client: typesenseInstantsearchAdapter.typesenseClient,
  formatTime: hit => getTimeForDateAndSlot(hit.date, hit.slot) || hit.time || hit.slot,
});

const search = instantsearch({
	  searchClient,
	  indexName:  siteConfig.typesenseCollection
});

const virtualSearchBox = instantsearch.connectors.connectSearchBox(() => {});

// Dropdown
const streamListDropdown = createDropdown(
  instantsearch.widgets.refinementList,
  { buttonText: siteConfig.facets.StreamName, 
    cssClasses: [ "navbar-brand"], closeOnChange: true, sortBy: ['count:desc'] },
);

const dateListDropdown = createDropdown(
  instantsearch.widgets.refinementList,
  { 
    cssClasses: ["navbar-brand"], 
    closeOnChange: true, 
    sortBy: (a, b) => {
      // Custom sorting logic: Sort by date number if present
      const matchA = a.name?.match(/\d+/);
      const matchB = b.name?.match(/\d+/);

      if (matchA && matchB) {
        return Number(matchA[0]) - Number(matchB[0]);
      }

      if (matchA) return -1;
      if (matchB) return 1;

      return (a.name ?? "").localeCompare(b.name ?? "");
    },

  },
);

const slotListDropdown = createDropdown(
  instantsearch.widgets.refinementList,
  { buttonText: "Time", closeOnChange: true, sortBy: ['name:asc'] },
);


const buildingListDropdown = createDropdown(
  instantsearch.widgets.refinementList,
  { closeOnChange: true },
);

const roomListDropdown = createDropdown(
  instantsearch.widgets.refinementList,
  { closeOnChange: true },
);

const clearDropdown = clearRefinements({
  container: "#clear-refinements",
  cssClasses: {
    button: [
      "btn",
      "btn-light",
      "navbar-brand",
    ],
  },
  templates: {
    resetLabel({ hasRefinements }, { html }) {
        return html`<span class=""
          >${hasRefinements ? "Clear refinements" : "No refinements"}</span
        >`;
    }
  }
});

const showRefinements = currentRefinements({
  container: "#current-refinements",
});

function initBioPopovers() {
  if (!window.bootstrap?.Popover) {
    return;
  }

  document
    .querySelectorAll('[data-bs-toggle="popover"]')
    .forEach((element) =>
      window.bootstrap.Popover.getOrCreateInstance(element, {
        customClass: "bio-popover",
      })
    );
}

//
// Initialize the search interface
search.addWidgets([
  virtualSearchBox({}),
 
  // Adding the refinementListDropdown widget on the `type` facet
  streamListDropdown({
    container: '#stream', // The CSS Selector of the DOM element inside which the widget is inserted.
    attribute: 'stream',  // The name of the attribute in the records.
    searchable: false,    // Whether to add a search input to let users search for more facet values.
    limit: 40,
  }),

  dateListDropdown({
    container: '#date', // The CSS Selector of the DOM element inside which the widget is inserted.
    attribute: 'date',  // The name of the attribute in the records.
    searchable: false    // Whether to add a search input to let users search for more facet values.
  }),

  slotListDropdown({
    container: '#slot', // The CSS Selector of the DOM element inside which the widget is inserted.
    attribute: 'slot',  // The name of the attribute in the records.
    searchable: false    // Whether to add a search input to let users search for more facet values.

  }),

  ...(siteConfig.facets.BuildingFacet ? [
    buildingListDropdown({
      container: '#building', // The CSS Selector of the DOM element inside which the widget is inserted.
      attribute: 'building',  // The name of the attribute in the records.
      searchable: false    // Whether to add a search input to let users search for more facet values.
    }),
  ] : []),

  ...(siteConfig.facets.RoomFacet ? [
    roomListDropdown({
      container: '#room', // The CSS Selector of the DOM element inside which the widget is inserted.
      attribute: 'room',  // The name of the attribute in the records.
      searchable: true    // Whether to add a search input to let users search for more facet values.
    }),
  ] : []),

  // clearDropdown,
  showRefinements,

  instantsearch.widgets.hits({
    container: '#hits',
    cssClasses: {
      root: 'row',
    //  item: 'col-lg-3 col-md-4 col-sm-6'
    },
    templates: {
       item: (hit, { html, components }) => html`
       <article>
          <div class="${hit['stream_class']+'-container'}">
            <div class="panel-heading-meta">
              <div class="panel-heading-time text-muted">
                <span><i class="bi-calendar" aria-hidden="true"></i> ${hit['date']}</span>
                <span><i class="bi-clock" aria-hidden="true"></i> ${getTimeForDateAndSlot(hit['date'], hit['slot']) || hit['time']}</span>
              </div>
              <button type="button" class="btn btn-outline-primary btn-sm schedule-select"
                data-session-id=${hit.objectID || hit.id}
                aria-pressed="false" aria-label="Add to my schedule" title="Add to my schedule"><i class="bi bi-bookmark-plus" aria-hidden="true"></i></button>
            </div>

            <div class="panel-heading-labels">
              <span class="badge rounded-pill ${hit['stream_class'] || 'bg-secondary'}">${hit['stream']}</span>
              ${ hit['panel_type'] ? html`
                <span class="badge rounded-pill bg-primary">${hit['panel_type']}</span>
              ` : '' }
            </div>

            <h5 class="card-title paper panel-heading-title">${components.Highlight({ hit, attribute: 'panel_title' })}</h5>
            <h6 class="card-subtitle paper">
              <a class="room link-underline-light link-opacity-75 link-secondary" href="${campusMapFiles[siteConfig.publicdir] || '#'}" target="_blank" rel="noopener noreferrer">
              <i class="bi-geo-alt-fill"/> ${components.Highlight({ hit, attribute: 'room' })} 
              ${ hit['building'] ? `(${hit['building']})` : '' }</a>


              ${ 
                hit['teams'] ? html`
                <a class="teams link-underline-light link-opacity-75 link-secondary" href="${hit['teams']}" target="_blank" rel="noopener noreferrer">
                  <i class="bi bi-person-video3"></i> Online Link
                </a>
              ` : siteConfig.results.showInPerson ? html`
                <span class="teams link-opacity-75 link-secondary">
                  <i class="bi bi-person-walking"></i> In Person Only 
                </span>` : html``
              }
            </h6>

            <p class="card-text">
    ${
            hit['preconstituted'] && hit['panel_type'] !== 'Pre-constituted panel'
              ? html`
                <ul class="list-group">
                  ${hit.panel_people.map(p => html`
                  <li class="list-group-item">
                          ${
                            p['bio']
                              ? html`
                                  <button
                                    type="button"
                                    class="btn btn-link btn-sm p-0 align-baseline bio-popover-trigger"
                                    data-bs-toggle="popover"
                                    data-bs-trigger="hover focus"
                                    data-bs-placement="top"
                                    data-bs-content=${p['bio']}
                                    title=${p['name']}
                                    aria-label=${`Show bio for ${p['authors']}`}
                                  >
                                    ${p['name']}
                                  </button>
                                  `
                              : html`${p['name']}`
                          }
                  </li>
                   `)}
                </ul>

                <div class="accordion panel_details" id="parent${hit['panel_id']}">
                    <div class="accordion-item">
                      <h2 class="accordion-header" id="header${hit['panel_id']}">
                        <button
                          class="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse_panel${hit['panel_id']}"
                          aria-expanded="false"
                          aria-controls="collapse_panel${hit['panel_id']}"
                        >
                          Panel Details
                        </button>
                      </h2>
                      <div
                        id="collapse_panel${hit['panel_id']}"
                        class="accordion-collapse collapse"
                        aria-labelledby="parent${hit['panel_id']}"
                        data-bs-parent="#parent${hit['panel_id']}"
                      >
                        <div class="accordion-body">
                        <div
                            class="preconstituted-html"
                            dangerouslySetInnerHTML=${{ __html: hit['panel_description_html'] }}
                          ></div>
                        </div>
                      </div>
                    </div>
                </div>
              ` : 

              html`
              <div class="accordion" id="parent${hit['panel_id']}">
                  ${hit.papers.map(p => html`
                    <div class="accordion-item">
                      <h2 class="accordion-header" id="header${p['paper_id']}">
                        <button
                          class="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse${p['paper_id']}"
                          aria-expanded="false"
                          aria-controls="collapse${p['paper_id']}"
                        >
                          ${p['authors']}, ${p['paper_title']}
                        </button>
                      </h2>
                      <div
                        id="collapse${p['paper_id']}"
                        class="accordion-collapse collapse"
                        aria-labelledby="header${p['paper_id']}"
                        data-bs-parent="#parent${hit['panel_id']}"
                      >
                        <div class="accordion-body">
                          <p>
                          <b>${p['paper_title']}</b><br/>
                          <!-- <i>${p['authors']}</i> -->
                          ${
                            p['bio']
                              ? html`
                                  <button
                                    type="button"
                                    class="btn btn-link btn-sm p-0 align-baseline bio-popover-trigger"
                                    data-bs-toggle="popover"
                                    data-bs-trigger="hover focus"
                                    data-bs-placement="top"
                                    data-bs-content=${p['bio']}
                                    title=${p['authors']}
                                    aria-label=${`Show bio for ${p['authors']}`}
                                  >
                                    <i class="bi bi-person-circle pre-authors"></i>
                                    <i>${p['authors']}</i>

                                  </button>
                                  `
                              : html``
                          }
                          </p>
                          <p>
                          ${p['abstract'] || 'No abstract provided.'}
                          </p>
                        </div>
                      </div>
                    </div>
                    `)}
               ${ hit['panel_description_html'] ? html`
                  <div class="accordion panel_details" id="parent${hit['panel_id']}">
                    <div class="accordion-item">
                      <h2 class="accordion-header" id="header${hit['panel_id']}">
                        <button
                          class="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapse_panel${hit['panel_id']}"
                          aria-expanded="false"
                          aria-controls="collapse_panel${hit['panel_id']}"
                        >
                          Panel Details
                        </button>
                      </h2>
                      <div
                        id="collapse_panel${hit['panel_id']}"
                        class="accordion-collapse collapse"
                        aria-labelledby="parent${hit['panel_id']}"
                        data-bs-parent="#parent${hit['panel_id']}"
                      >
                        <div class="accordion-body">
                        <div
                            class="preconstituted-html panel_abstract"
                            dangerouslySetInnerHTML=${{ __html: hit['panel_description_html'] }}
                          ></div>
                        </div>
                      </div>
                    </div>
                </div>                
              ` : html ``}


                </div>`}
              
                

          ${siteConfig.results.showChair && hit.stream !== "Forum"
                ? html`<i class="chair float-end">Chair: ${hit.chair || "TBC"}</i>`
                : html``}
          
        </p>


          </div>
        </article>
      `,
          },    
  }),

  instantsearch.widgets.configure({
    hitsPerPage: 30,
  }),

  instantsearch.widgets.pagination({
    container: '#pagination',
  }),
]);

search.on("render", () => { initBioPopovers(); personalSchedule.updateButtons(); });
search.start();

/*
            <ul  class="list-group">
            ${hit.papers.map(p => 
              html`<li  class="list-group-item">${p['author']}, "${p['paper_title']}"</li>`)}
            </ul>
*/

const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
  key: 'instantsearch',
  limit: 5,
  transformSource({ source }) {
    return {
      ...source,
      onSelect({ setIsOpen, setQuery, item, event }) {
        onSelect({ setQuery, setIsOpen, event, query: item.label });
      },
    };
  },
});

const querySuggestionsPlugin = createQuerySuggestionsPlugin({
  searchClient,
  indexName: 'instant_search_demo_query_suggestions',
  getSearchParams() {
    return recentSearchesPlugin.data.getAlgoliaSearchParams({ hitsPerPage: 6 });
  },
  transformSource({ source }) {
    return {
      ...source,
      sourceId: 'querySuggestionsPlugin',
      onSelect({ setIsOpen, setQuery, event, item }) {
        onSelect({ setQuery, setIsOpen, event, query: item.query });
      },
      getItems(params) {
        if (!params.state.query) {
          return [];
        }

        return source.getItems(params);
      },
    };
  },
});


// Fulltext Search box
autocomplete({
  container: '#searchbox',
  openOnFocus: true,
  detachedMediaQuery: 'none',
  onStateChange({ state }) {
    debouncedSetInstantSearchUiState({ query: state.query });
  },
  onSubmit({ state }) {
    setInstantSearchUiState({ query: state.query });
  },
  plugins: [recentSearchesPlugin, querySuggestionsPlugin],
});

function debounce(fn, delay) {
  let timeoutId;

  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => fn(...args), delay);
  };
}

function setInstantSearchUiState(indexUiState) {
  search.mainIndex.setIndexUiState({ page: 1, ...indexUiState });
}

const debouncedSetInstantSearchUiState = debounce(setInstantSearchUiState, 250);

function onSelect({ setIsOpen, setQuery, event, query }) {
  if (isModifierEvent(event)) {
    return;
  }

  setQuery(query);
  setIsOpen(false);
  setInstantSearchUiState({ query });
}

function isModifierEvent(event) {
  const isMiddleClick = event.button === 1;

  return (
    isMiddleClick ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey
  );
}
